// Comprehensive Theme Configuration
// All colors and design tokens used across the application

export const colors = {
  // Background colors — warm redesign palette
  bg: {
    primary: {
      light: '#f7f4ee',
      dark: '#0a0908',
    },
    secondary: {
      light: '#fffdf9',
      dark: '#100f0d',
    },
    hover: {
      light: 'rgba(26, 21, 16, 0.05)',
      dark: 'rgba(244, 242, 239, 0.05)',
    },
    subtle: {
      light: 'rgba(26, 21, 16, 0.02)',
      dark: 'rgba(244, 242, 239, 0.02)',
    },
  },

  // Text colors — warm redesign palette
  text: {
    primary: {
      light: '#1a1510',
      dark: '#f4f2ef',
    },
    secondary: {
      light: 'rgba(26, 21, 16, 0.72)',
      dark: 'rgba(244, 242, 239, 0.80)',
    },
    muted: {
      light: 'rgba(26, 21, 16, 0.55)',
      dark: 'rgba(244, 242, 239, 0.54)',
    },
  },

  // Border colors — warm redesign palette
  border: {
    default: {
      light: 'rgba(26, 21, 16, 0.12)',
      dark: '#23201b',
    },
    light: {
      light: 'rgba(26, 21, 16, 0.08)',
      dark: '#1c1a17',
    },
  },

  // Shadow colors
  shadow: {
    light: 'rgba(15, 23, 42, 0.08)',
    dark: 'rgba(0, 0, 0, 0.6)',
  },

  // Primary brand color — blue. Used for CTAs, buttons, links, active/selected
  // states. Matches Tailwind's standard blue scale; MUI's palette.primary and
  // tailwind.config.js both read their primary color from here.
  primary: {
    light: '#2563eb', // 600 — light-mode CTA
    dark: '#3b82f6', // 500 — dark-mode CTA
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },

  // Accent colors - Primary palette
  accent: {
    // Amber / gold — secondary/highlight accent (badges, streaks, rank markers). Not used for CTAs/buttons.
    // Only `dark` is referenced directly — the widely-used `--accent` CSS var in
    // index.css holds this same value independently, not sourced from here.
    amber: {
      dark: '#e8b046',
    },
    blue: {
      dark: '#5b8cff',
      300: '#83a8ff',
      400: '#5b8cff',
      500: '#4a78ea',
      600: '#3f6fd6',
      700: '#3357b0',
    },
    purple: {
      light: '#7c3aed',
      dark: '#a273e8',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      700: '#7c3aed',
      800: '#6b21a8',
    },
    green: {
      dark: '#4ec38a',
      300: '#8fddb8',
      400: '#4ec38a',
      500: '#2fae74',
      600: '#1f9d63',
    },
    red: {
      light: '#d9503a',
      dark: '#e0664f',
      300: '#e98873',
      400: '#e0664f',
      500: '#d9503a',
      600: '#c24632',
    },
    // Orange kept as a warm amber ramp — the redesign has a single warm accent (gold)
    orange: {
      dark: '#e8b046',
      300: '#edc06a',
      400: '#e8b046',
      500: '#d99a2b',
      600: '#c98a12',
    },
    pink: {
      light: '#ec4899',
    },
  },

  // Semantic colors — warm redesign palette
  success: {
    main: '#4ec38a',
    bg: 'rgba(78, 195, 138, 0.14)',
    border: 'rgba(78, 195, 138, 0.30)',
  },
  error: {
    main: '#e0664f',
    bg: 'rgba(224, 102, 79, 0.14)',
    border: 'rgba(224, 102, 79, 0.32)',
  },
  warning: {
    main: '#e8b046',
    bg: 'rgba(232, 176, 70, 0.16)',
    border: 'rgba(232, 176, 70, 0.38)',
  },
  info: {
    main: '#5b8cff',
    dark: '#3f6fd6',
    bg: 'rgba(91, 140, 255, 0.14)',
    border: 'rgba(91, 140, 255, 0.30)',
  },

  // Yes/No outcome pill — shared by every binary-outcome picker (election
  // cards, question cards, sports props, trade side selectors).
  chip: {
    yes: {
      subtleBg: '#eef8f2',
      bg: '#dcf3e8',
      hoverBg: '#bfe9d4',
      text: '#1a5f40',
    },
    no: {
      subtleBg: '#fbeeea',
      bg: '#f7e0da',
      hoverBg: '#f5d0c8',
      text: '#a33b29',
    },
  },

  // Neutral colors (slate-based)
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Common colors (black, white - used for Apple button, etc.)
  common: {
    black: '#000000',
    white: '#ffffff',
    blackHover: '#1a1a1a',
  },

  // Brand colors for special cards
  brand: {
    netflix: '#e50914',
    google: {
      blue: '#4285F4',
      green: '#34A853',
      yellow: '#FBBC05',
      red: '#EA4335',
    },
    whatsapp: '#25D366',
    // Chain/token brand colors — single source of truth for the deposit/withdraw flows,
    // which previously defined this same list independently in 2-3 places.
    crypto: {
      ethereum: '#627EEA',
      polygon: '#8247E5',
      arbitrum: '#28A0F0',
      base: '#0052FF',
      optimism: '#FF0420',
      solana: '#9945FF',
      bitcoin: '#F7931A',
      tron: '#FF060A',
      bsc: '#F0B90B',
      avalanche: '#E84142',
      usdc: '#2775CA',
      usdt: '#26A17B',
      pusd: '#10B981',
      pyusd: '#0070BA',
      dai: '#F5AC37',
    },
  },
} as const;

// Spacing scale (matching Tailwind)
export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
} as const;

// Border radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  DEFAULT: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
} as const;

// Font sizes
export const fontSize = {
  xs: ['0.75rem', { lineHeight: '1rem' }],
  sm: ['0.875rem', { lineHeight: '1.25rem' }],
  base: ['1rem', { lineHeight: '1.5rem' }],
  lg: ['1.125rem', { lineHeight: '1.75rem' }],
  xl: ['1.25rem', { lineHeight: '1.75rem' }],
  '2xl': ['1.5rem', { lineHeight: '2rem' }],
  '3xl': ['1.875rem', { lineHeight: '2.25rem' }],
  '4xl': ['2.25rem', { lineHeight: '2.5rem' }],
} as const;

// Font weights
export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

// Box shadows
export const boxShadow = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  card: '0 4px 20px rgba(15, 23, 42, 0.08)',
  cardHover: '0 8px 30px rgba(15, 23, 42, 0.12)',
  button: '0 4px 16px rgba(232, 176, 70, 0.35)',
  success: '0 4px 12px rgba(78, 195, 138, 0.35)',
  error: '0 4px 12px rgba(224, 102, 79, 0.35)',
} as const;

// Transitions
export const transition = {
  fast: '0.15s ease',
  DEFAULT: '0.2s ease',
  slow: '0.3s ease',
} as const;

// Z-index scale
export const zIndex = {
  dropdown: 1000,
  modal: 2000,
  tooltip: 3000,
} as const;

// Breakpoints (matching Tailwind)
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// Export type for theme mode
export type ThemeMode = 'light' | 'dark';
